import type { ContractedBook } from '@/data/types';

export type BrageKategori = 'skjonn' | 'barnebok' | 'sakprosa' | 'apen';

export const BRAGE_KATEGORIER: ReadonlyArray<BrageKategori> = [
  'skjonn',
  'barnebok',
  'sakprosa',
  'apen',
];

export const KATEGORI_NAVN: Record<BrageKategori, string> = {
  skjonn: 'Skjønnlitteratur',
  barnebok: 'Barne- og ungdomsbok',
  sakprosa: 'Sakprosa',
  apen: 'Åpen klasse',
};

export const SJANGER_TIL_KATEGORI: Record<string, BrageKategori> = {
  LITERARY: 'skjonn',
  POETRY: 'skjonn',
  KRIM: 'apen',
  FANTASY: 'apen',
  ROMANCE: 'apen',
  FEELGOOD: 'apen',
  BARNEBOK: 'barnebok',
  NONFICTION: 'sakprosa',
};

export const BRAGE_NOMINASJON_DAG = 90;
export const BRAGE_HINT_DAG = 95;
export const BRAGE_VINNER_DAG = 100;
export const BRAGE_AAR_LENGDE = 365;
export const NOMINASJONER_PER_KATEGORI = 5;
export const VINNER_BOOST_FAKTOR = 3.0;
export const VINNER_BOOST_DAGER = 60;
export const NOMINASJON_BOOST_FAKTOR = 2.0;
export const NOMINASJON_BOOST_DAGER = 30;
export const VINNER_OMDOMME = 30;
export const VINNER_LOJALITET = 50;
export const NOMINASJON_OMDOMME = 10;

export type BrageNominasjon = {
  bokIsbn: string;
  tittel: string;
  forfatterNavn: string;
  forlag: 'spiller' | string;
  score: number;
  kategori: BrageKategori;
};

export type BrageVinner = BrageNominasjon;

export type BrageStatus = {
  aar: number;
  fase: 'nominasjon' | 'vinner_annonsert' | 'avsluttet';
  nominasjoner: Record<BrageKategori, BrageNominasjon[]>;
  vinnere: Record<BrageKategori, BrageVinner | null>;
};

export type BrageSalgsBoost = {
  isbn: string;
  faktor: number;
  ferdigDag: number;
};

export function kategoriForSjanger(sjanger: string): BrageKategori {
  const key = sjanger.trim().toUpperCase();
  return SJANGER_TIL_KATEGORI[key] ?? 'apen';
}

export function tomNominasjonsKart(): Record<BrageKategori, BrageNominasjon[]> {
  return {
    skjonn: [],
    barnebok: [],
    sakprosa: [],
    apen: [],
  };
}

export function tomVinnerKart(): Record<BrageKategori, BrageVinner | null> {
  return {
    skjonn: null,
    barnebok: null,
    sakprosa: null,
    apen: null,
  };
}

export function beregnBrageScore(
  anmeldelsesSnitt: number,
  salg: number,
  omdomme: number,
): number {
  const anmDel = clamp01((anmeldelsesSnitt - 1) / 5);
  const salgDel = clamp01(Math.log10(salg + 1) / 5);
  const omdDel = clamp01(omdomme / 100);
  return 0.5 * anmDel + 0.3 * salgDel + 0.2 * omdDel;
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

export type SpillerBokKandidat = {
  bok: ContractedBook;
  forfatterNavn: string;
  scoreFaktor?: number;
};

export type KonkurrentBokKandidat = {
  isbn: string;
  tittel: string;
  forfatterNavn: string;
  forlag: string;
  sjanger: string;
  anmeldelsesSnitt: number;
  salg: number;
};

export function velgNominasjoner(
  kategori: BrageKategori,
  spillerKandidater: ReadonlyArray<SpillerBokKandidat>,
  konkurrentKandidater: ReadonlyArray<KonkurrentBokKandidat>,
  spillerOmdomme: number,
): BrageNominasjon[] {
  const alle: Array<BrageNominasjon & { erSpiller: boolean }> = [];

  for (const kand of spillerKandidater) {
    if (kategoriForSjanger(kand.bok.genre) !== kategori) continue;
    const snitt = anmeldelseSnittFor(kand.bok);
    const grunnScore = beregnBrageScore(snitt, kand.bok.salg, spillerOmdomme);
    const faktor = kand.scoreFaktor ?? 1;
    const score = grunnScore * faktor;
    alle.push({
      bokIsbn: kand.bok.isbn,
      tittel: kand.bok.title,
      forfatterNavn: kand.forfatterNavn,
      forlag: 'spiller',
      score,
      kategori,
      erSpiller: true,
    });
  }

  for (const kand of konkurrentKandidater) {
    if (kategoriForSjanger(kand.sjanger) !== kategori) continue;
    const score = beregnBrageScore(kand.anmeldelsesSnitt, kand.salg, 50);
    alle.push({
      bokIsbn: kand.isbn,
      tittel: kand.tittel,
      forfatterNavn: kand.forfatterNavn,
      forlag: kand.forlag,
      score,
      kategori,
      erSpiller: false,
    });
  }

  alle.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    if (a.erSpiller && !b.erSpiller) return -1;
    if (!a.erSpiller && b.erSpiller) return 1;
    return 0;
  });

  return alle.slice(0, NOMINASJONER_PER_KATEGORI).map((n) => ({
    bokIsbn: n.bokIsbn,
    tittel: n.tittel,
    forfatterNavn: n.forfatterNavn,
    forlag: n.forlag,
    score: n.score,
    kategori: n.kategori,
  }));
}

export function velgVinner(
  nominasjoner: ReadonlyArray<BrageNominasjon>,
  rng: () => number,
): BrageVinner | null {
  if (nominasjoner.length === 0) return null;
  const sortert = [...nominasjoner].sort((a, b) => b.score - a.score);
  if (rng() < 0.7) {
    return { ...sortert[0] };
  }
  const top3 = sortert.slice(0, Math.min(3, sortert.length));
  const idx = Math.floor(rng() * top3.length);
  return { ...top3[Math.min(top3.length - 1, idx)] };
}

export function anmeldelseSnittFor(bok: ContractedBook): number {
  if (bok.anmeldelser.length === 0) return 0;
  let sum = 0;
  for (const a of bok.anmeldelser) sum += a.terninger;
  return sum / bok.anmeldelser.length;
}

export function bokInnenforAar(
  bok: ContractedBook,
  dag: number,
): boolean {
  if (bok.utgivelsesDag === null) return false;
  const diff = dag - bok.utgivelsesDag;
  return diff >= 0 && diff <= BRAGE_AAR_LENGDE;
}

const KONKURRENT_FORLAG: ReadonlyArray<{ id: string; navn: string }> = [
  { id: 'kapelle_damm', navn: 'Kapelle Damm' },
  { id: 'asketank', navn: 'Asketank' },
  { id: 'gullsand', navn: 'Gullsand' },
  { id: 'boenkulant', navn: 'Boenkulant' },
];

const SYNTH_TITLER: Record<BrageKategori, ReadonlyArray<string>> = {
  skjonn: [
    'Stillheten etter mor',
    'Vinternatt i Vestfold',
    'Brev fra en avbrutt sommer',
    'Det vi ikke sa',
    'Lykken er en tunnel',
    'Aske og ravn',
    'Den siste poeten',
  ],
  barnebok: [
    'Mira og den magiske parken',
    'Tor på rømmen',
    'Onkel Bjørns store hemmelighet',
    'Lille pinnsvin våkner',
    'Pia og spøkelses-katten',
    'Kaptein Klaus seiler nord',
  ],
  sakprosa: [
    'Klimaet etter oss',
    'Norge under okkupasjon — en ny lesning',
    'Hjernen på overtid',
    'Fjellene som forsvant',
    'Sykepleiernes krig',
    'Stortinget bak kulissene',
  ],
  apen: [
    'Blodspor i Bergen',
    'Drømmer av jern',
    'Sommerferien som forsvant',
    'Hjertekorset',
    'Skyggen på Sognefjorden',
    'Den siste detektiven',
    'Glasshjertet',
  ],
};

const SYNTH_FORNAVN: ReadonlyArray<string> = [
  'Eira',
  'Mats',
  'Sigrid',
  'Henrik',
  'Tora',
  'Lars',
  'Mari',
  'Ola',
  'Ingrid',
  'Erik',
  'Astrid',
  'Bjørn',
];

const SYNTH_ETTERNAVN: ReadonlyArray<string> = [
  'Berg',
  'Lund',
  'Solheim',
  'Holm',
  'Dahl',
  'Kvam',
  'Stene',
  'Aas',
  'Vik',
  'Moe',
  'Rønning',
  'Sundet',
];

function mulberry32(seed: number): () => number {
  let t = seed >>> 0;
  return function (): number {
    t = (t + 0x6d2b79f5) >>> 0;
    let r = t;
    r = Math.imul(r ^ (r >>> 15), r | 1);
    r ^= r + Math.imul(r ^ (r >>> 7), r | 61);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

export function genererKonkurrentBoker(
  aar: number,
): KonkurrentBokKandidat[] {
  const rng = mulberry32(aar * 9173 + 1);
  const ut: KonkurrentBokKandidat[] = [];
  const sjangerPerKategori: Record<BrageKategori, ReadonlyArray<string>> = {
    skjonn: ['LITERARY', 'POETRY'],
    barnebok: ['BARNEBOK'],
    sakprosa: ['NONFICTION'],
    apen: ['KRIM', 'FANTASY', 'ROMANCE', 'FEELGOOD'],
  };

  for (const kategori of BRAGE_KATEGORIER) {
    const titler = SYNTH_TITLER[kategori];
    const sjangerListe = sjangerPerKategori[kategori];
    const antall = 3 + Math.floor(rng() * 3);
    for (let i = 0; i < antall; i += 1) {
      const tIdx = Math.floor(rng() * titler.length);
      const fIdx = Math.floor(rng() * SYNTH_FORNAVN.length);
      const eIdx = Math.floor(rng() * SYNTH_ETTERNAVN.length);
      const fIdxF = Math.floor(rng() * KONKURRENT_FORLAG.length);
      const sIdx = Math.floor(rng() * sjangerListe.length);
      const tittel = titler[tIdx];
      const navn = `${SYNTH_FORNAVN[fIdx]} ${SYNTH_ETTERNAVN[eIdx]}`;
      const forlag = KONKURRENT_FORLAG[fIdxF];
      const sjanger = sjangerListe[sIdx];
      const snitt = 3.0 + rng() * 2.0;
      const salg = Math.floor(2000 + rng() * 40_000);
      ut.push({
        isbn: `synth_${aar}_${kategori}_${i}`,
        tittel,
        forfatterNavn: navn,
        forlag: forlag.navn,
        sjanger,
        anmeldelsesSnitt: snitt,
        salg,
      });
    }
  }
  return ut;
}

export function flaggVinner(aar: number, kategori: BrageKategori): string {
  return `brage_vinner_${aar}_${kategori}`;
}

export function flaggNominert(aar: number, isbn: string): string {
  return `brage_nominert_${aar}_${isbn}`;
}
